import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ActionIconButton, DataViewActionCell } from '../components/DataViewActionCell';
import { DataTable, type TableColumn } from '../components/DataTable';
import { EntityCreateDialog, EntityManageDialog } from '../components/EntityDialogs';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../components/Toast';
import { Button, Card, CheckboxField, Field, Select, TextArea, TextInput } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedResource } from '../hooks/usePaginatedResource';
import { useT, translate, userError, LocalizedError } from '../i18n/messages';
import {
  describeError,
  emptyApplicationForm,
  emptyCredentialForm,
  emptyWebhookForm,
  formatCredential,
  formatDomains,
  formatWebhook,
  parseWebhookInput,
  toApplicationInput,
  toCredentialInput,
  type ApplicationFormState,
  type CredentialFormState,
  type WebhookFormState,
} from '../lib/adminForms';
import { type ApplicationRecord, billingApi } from '../lib/api';
import {
  applicationStatusOptions,
  credentialScopeOptions,
  credentialStatusOptions,
  environmentOptions,
  webhookStatusOptions,
} from '../lib/options';

function formatDomainSummary(application: ApplicationRecord) {
  if (application.domains.length === 0) {
    return '—';
  }

  return application.domains
    .map(
      (entry) =>
        `${entry.domain} (${entry.environment}${entry.is_primary ? `, ${translate('billing.column.primary')}` : ''})`,
    )
    .join(', ');
}

function listSummary(values: string[]) {
  return values.length > 0 ? values.join(', ') : '—';
}

function parseCredentialScopes(text: string) {
  return text
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

interface DomainFormState {
  domain: string;
  environment: string;
  is_primary: boolean;
}

const emptyDomainForm: DomainFormState = {
  domain: '',
  environment: 'prod',
  is_primary: false,
};

function Section({
  actions,
  children,
  title,
}: {
  actions?: ReactNode;
  children: ReactNode;
  title: string;
}) {
  return (
    <section className="panel page-panel">
      <div className="panel-heading">
        <h2>{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function ConfirmDialog({
  busy = false,
  description,
  onClose,
  onConfirm,
  open,
  title,
}: {
  busy?: boolean;
  description: string;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  title: string;
}) {
  const t = useT();
  return (
    <EntityManageDialog onClose={onClose} open={open} size="small" title={title}>
      <div className="entity-stack">
        <p>{description}</p>
        <div className="dialog-actions">
          <Button disabled={busy} onClick={onClose} type="button" variant="secondary">
            {t('billing.ui.cancel')}
          </Button>
          <Button disabled={busy} onClick={onConfirm} type="button" variant="danger">
            {t('billing.ui.confirm')}
          </Button>
        </div>
      </div>
    </EntityManageDialog>
  );
}

function ApplicationFormFields({
  form,
  onChange,
}: {
  form: ApplicationFormState;
  onChange: (next: ApplicationFormState) => void;
}) {
  const t = useT();
  return (
    <div className="form-grid">
      <div className="form-row form-row--split">
        <Field label={t('billing.ui.application.key')}>
          <TextInput
            onChange={(event) => onChange({ ...form, key: event.target.value })}
            placeholder="billing_app"
            value={form.key}
          />
        </Field>
        <Field label={t('billing.column.displayName')}>
          <TextInput
            onChange={(event) => onChange({ ...form, display_name: event.target.value })}
            placeholder={t('billing.ui.billing.app')}
            value={form.display_name}
          />
        </Field>
      </div>
      <div className="form-row form-row--split">
        <Field label={t('billing.column.status')}>
          <Select
            allowClear={false}
            onChange={(value) => onChange({ ...form, status: value ?? 'active' })}
            options={applicationStatusOptions}
            value={form.status}
          />
        </Field>
        <Field
          description={t(
            'billing.ui.used.for.default.webhook.routing.and.environment.specific.settings',
          )}
          label={t('billing.ui.default.environment')}
        >
          <Select
            allowClear
            onChange={(value) => onChange({ ...form, default_environment: value ?? '' })}
            options={environmentOptions}
            placeholder={t('billing.ui.select.an.environment')}
            value={form.default_environment}
          />
        </Field>
      </div>
      <Field label={t('billing.column.description')}>
        <TextArea
          onChange={(event) => onChange({ ...form, description: event.target.value })}
          placeholder={t('billing.ui.short.description.for.administrators')}
          rows={3}
          value={form.description}
        />
      </Field>
    </div>
  );
}

export function ApplicationsPage() {
  const { tokens } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [applicationForm, setApplicationForm] =
    useState<ApplicationFormState>(emptyApplicationForm);
  const resource = usePaginatedResource<ApplicationRecord>(
    tokens?.access_token ?? '',
    page,
    billingApi.getApplications,
  );

  const columns: TableColumn<ApplicationRecord>[] = [
    { key: 'key', label: 'billing.column.key' },
    { key: 'display_name', label: 'billing.column.displayName' },
    { key: 'status', label: 'billing.column.status', maxWidth: 120 },
    {
      key: 'domains',
      label: 'billing.column.domains',
      render: (application) => formatDomainSummary(application),
      searchValue: (application) => formatDomainSummary(application),
    },
    {
      key: 'service_clients',
      label: 'billing.column.serviceClients',
      maxWidth: 100,
      render: (application) => String(application.service_clients.length),
      sortValue: (application) => application.service_clients.length,
    },
    {
      key: 'webhook_endpoints',
      label: 'billing.column.webhooks',
      maxWidth: 100,
      render: (application) => String(application.webhook_endpoints.length),
      sortValue: (application) => application.webhook_endpoints.length,
    },
    {
      key: 'actions',
      label: 'billing.column.actions',
      className: 'table-actions-cell',
      truncate: false,
      render: (application) => (
        <DataViewActionCell>
          <ActionIconButton
            icon="products"
            label={t('billing.ui.view.products')}
            onClick={() => navigate(`/products?application_id=${application.id}`)}
          />
          <ActionIconButton
            icon="manage"
            label={t('billing.ui.manage.application')}
            onClick={() => navigate(`/applications/${application.id}`)}
          />
        </DataViewActionCell>
      ),
    },
  ];

  async function submitCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!tokens?.access_token) {
      return;
    }

    try {
      const created = await billingApi.createApplication(
        tokens.access_token,
        toApplicationInput(applicationForm),
      );
      toast.success(t('billing.ui.application.created'));
      setCreateOpen(false);
      setApplicationForm(emptyApplicationForm);
      resource.reload();
      navigate(`/applications/${created.id}`);
    } catch (error) {
      toast.error(describeError(error));
    }
  }

  if (!tokens) {
    return <section className="panel page-panel">{t('billing.error.unauthorized')}</section>;
  }

  return (
    <section className="page" data-testid="billing-applications-page">
      <PageHeader
        description={t('billing.ui.applications.are.managed.from.their.dedicated.manage.pages')}
        title={t('billing.page.applications')}
        actions={
          <Button onClick={() => setCreateOpen(true)} type="button">
            {t('billing.ui.create.application')}
          </Button>
        }
      />

      {resource.status === 'loading' ? (
        <section className="panel page-panel">{t('billing.resource.loading')}</section>
      ) : null}

      {resource.status === 'error' ? (
        <Card className="panel panel-error page-panel">
          <p>{userError(resource.error, t)}</p>
          <Button onClick={resource.reload} type="button">
            {t('billing.resource.retry')}
          </Button>
        </Card>
      ) : null}

      {resource.status === 'success' && resource.data ? (
        <>
          <DataTable
            columns={columns}
            emptyMessage={t('billing.ui.no.applications.have.been.created.yet')}
            rows={resource.data.items}
            searchable
            searchPlaceholder={t('billing.ui.search.applications')}
          />
          <footer className="pagination">
            <Button
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              type="button"
              variant="secondary"
            >
              {t('billing.resource.previous')}
            </Button>
            <span className="pagination-summary">
              {t('billing.pagination.summary', {
                page: resource.data.pagination.page,
                total: Math.max(resource.data.pagination.total_pages, 1),
              })}
            </span>
            <Button
              disabled={
                resource.data.pagination.total_pages === 0 ||
                page >= resource.data.pagination.total_pages
              }
              onClick={() => setPage((current) => current + 1)}
              type="button"
              variant="secondary"
            >
              {t('billing.resource.next')}
            </Button>
          </footer>
        </>
      ) : null}

      <EntityCreateDialog
        onClose={() => {
          setCreateOpen(false);
          setApplicationForm(emptyApplicationForm);
        }}
        open={createOpen}
        size="large"
        title={t('billing.ui.create.application')}
      >
        <form onSubmit={(event) => void submitCreate(event)}>
          <ApplicationFormFields form={applicationForm} onChange={setApplicationForm} />
          <div className="dialog-actions">
            <Button type="submit">{t('billing.ui.create.application')}</Button>
            <Button
              onClick={() => {
                setCreateOpen(false);
                setApplicationForm(emptyApplicationForm);
              }}
              type="button"
              variant="secondary"
            >
              {t('billing.ui.cancel')}
            </Button>
          </div>
        </form>
      </EntityCreateDialog>
    </section>
  );
}

export function ApplicationManagePage() {
  const { applicationId } = useParams();
  const { tokens } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const [banner, setBanner] = useState<{
    error: boolean;
    message: string;
  } | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [application, setApplication] = useState<ApplicationRecord | null>(null);
  const [applicationForm, setApplicationForm] =
    useState<ApplicationFormState>(emptyApplicationForm);
  const [webhookForm, setWebhookForm] = useState<WebhookFormState>(emptyWebhookForm);
  const [credentialForm, setCredentialForm] = useState<CredentialFormState>(emptyCredentialForm);
  const [domainForm, setDomainForm] = useState<DomainFormState>(emptyDomainForm);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [credentialDialogOpen, setCredentialDialogOpen] = useState(false);
  const [credentialSecret, setCredentialSecret] = useState<{
    displayName: string;
    secret: string;
  } | null>(null);
  const [confirmState, setConfirmState] = useState<null | {
    kind: 'application' | 'domain' | 'webhook' | 'credential';
    id?: string;
  }>(null);
  const [busy, setBusy] = useState(false);
  const selectedCredentialScopes = useMemo(
    () => new Set(parseCredentialScopes(credentialForm.allowedScopesText)),
    [credentialForm.allowedScopesText],
  );

  async function loadApplication() {
    if (!tokens?.access_token || !applicationId) {
      return;
    }

    setStatus('loading');
    try {
      const response = await billingApi.listApplications(tokens.access_token);
      const nextApplication = response.items.find((entry) => entry.id === applicationId) ?? null;
      if (!nextApplication) {
        throw new LocalizedError('billing.ui.application.was.not.found');
      }
      setApplication(nextApplication);
      setApplicationForm({
        id: nextApplication.id,
        key: nextApplication.key,
        display_name: nextApplication.display_name,
        description: nextApplication.description ?? '',
        status: nextApplication.status,
        default_environment: nextApplication.default_environment ?? '',
        domainsText: formatDomains(nextApplication),
      });
      setStatus('success');
    } catch (error) {
      setBanner({ error: true, message: describeError(error) });
      setStatus('error');
    }
  }

  useEffect(() => {
    void loadApplication();
  }, [applicationId, tokens?.access_token]);

  const webhookColumns = useMemo<TableColumn<ApplicationRecord['webhook_endpoints'][number]>[]>(
    () => [
      {
        key: 'environment',
        label: 'billing.column.environment',
      },
      { key: 'url', label: 'billing.column.webhook' },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      {
        key: 'subscribed_event_types',
        label: 'billing.column.eventType',
        render: (webhook) => listSummary(webhook.subscribed_event_types),
        searchValue: (webhook) => listSummary(webhook.subscribed_event_types),
      },
      {
        key: 'signing_secret_last_four',
        label: 'billing.column.details',
        render: (webhook) =>
          webhook.signing_secret_last_four
            ? `••••${webhook.signing_secret_last_four}`
            : t('billing.ui.missing'),
        maxWidth: 120,
      },
      {
        key: 'actions',
        label: 'billing.column.actions',
        className: 'table-actions-cell',
        truncate: false,
        render: (webhook) => (
          <DataViewActionCell>
            <ActionIconButton
              icon="manage"
              label={t('billing.ui.manage.webhook')}
              onClick={() => {
                if (!application) {
                  return;
                }
                setWebhookForm(formatWebhook(application, webhook.id));
                setWebhookDialogOpen(true);
              }}
            />
          </DataViewActionCell>
        ),
      },
    ],
    [application],
  );

  const credentialColumns = useMemo<TableColumn<ApplicationRecord['service_clients'][number]>[]>(
    () => [
      { key: 'display_name', label: 'billing.column.displayName' },
      { key: 'credential_prefix', label: 'billing.column.key' },
      {
        key: 'environment',
        label: 'billing.column.environment',
        maxWidth: 120,
      },
      {
        key: 'secret_last_four',
        label: 'billing.column.secret',
        render: (credential) => `••••${credential.secret_last_four}`,
        searchValue: (credential) => credential.secret_last_four,
      },
      {
        key: 'allowed_scopes',
        label: 'billing.column.details',
        render: (credential) => listSummary(credential.allowed_scopes),
        searchValue: (credential) => listSummary(credential.allowed_scopes),
      },
      { key: 'status', label: 'billing.column.status', maxWidth: 120 },
      {
        key: 'actions',
        label: 'billing.column.actions',
        className: 'table-actions-cell',
        truncate: false,
        render: (credential) => (
          <DataViewActionCell>
            <ActionIconButton
              icon="manage"
              label={t('billing.ui.manage.credentials')}
              onClick={() => {
                if (!application) {
                  return;
                }
                setCredentialForm(formatCredential(application, credential.id));
                setCredentialDialogOpen(true);
              }}
            />
          </DataViewActionCell>
        ),
      },
    ],
    [application],
  );

  const domainColumns = useMemo<TableColumn<ApplicationRecord['domains'][number]>[]>(
    () => [
      { key: 'domain', label: 'billing.column.domain' },
      {
        key: 'environment',
        label: 'billing.column.environment',
        maxWidth: 120,
      },
      {
        key: 'is_primary',
        label: 'billing.column.primary',
        maxWidth: 100,
        render: (domain) => (domain.is_primary ? t('billing.status.yes') : t('billing.status.no')),
        sortValue: (domain) => Number(domain.is_primary),
      },
      {
        key: 'actions',
        label: 'billing.column.actions',
        className: 'table-actions-cell',
        truncate: false,
        render: (domain) => (
          <DataViewActionCell>
            <ActionIconButton
              icon="delete"
              label={t('billing.ui.delete.domain')}
              onClick={() => setConfirmState({ kind: 'domain', id: domain.domain })}
            />
          </DataViewActionCell>
        ),
      },
    ],
    [t],
  );

  async function saveApplicationDomains(domains: ApplicationRecord['domains']) {
    if (!tokens?.access_token || !application) {
      return;
    }

    await billingApi.updateApplication(tokens.access_token, application.id, {
      key: application.key,
      display_name: application.display_name,
      description: application.description,
      status: application.status,
      default_environment: application.default_environment,
      domains,
    });
  }

  async function submitApplication(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!tokens?.access_token || !application) {
      return;
    }

    setBusy(true);
    try {
      await billingApi.updateApplication(
        tokens.access_token,
        application.id,
        toApplicationInput(applicationForm),
      );
      toast.success(t('billing.ui.application.updated'));
      await loadApplication();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function submitWebhook(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!tokens?.access_token || !application) {
      return;
    }

    setBusy(true);
    try {
      const input = parseWebhookInput(webhookForm);
      if (webhookForm.id) {
        await billingApi.updateApplicationWebhook(
          tokens.access_token,
          application.id,
          webhookForm.id,
          input,
        );
      } else {
        await billingApi.createApplicationWebhook(tokens.access_token, application.id, input);
      }
      toast.success(
        webhookForm.id ? t('billing.ui.webhook.updated') : t('billing.ui.webhook.created'),
      );
      setWebhookDialogOpen(false);
      setWebhookForm(emptyWebhookForm);
      await loadApplication();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function submitCredential(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!tokens?.access_token || !application) {
      return;
    }

    setBusy(true);
    try {
      const input = toCredentialInput(credentialForm);
      if (credentialForm.id) {
        await billingApi.updateApplicationCredential(
          tokens.access_token,
          application.id,
          credentialForm.id,
          input,
        );
        toast.success(t('billing.ui.credentials.updated'));
      } else {
        const credential = await billingApi.createApplicationCredential(
          tokens.access_token,
          application.id,
          input,
        );
        if (credential.secret) {
          setCredentialSecret({
            displayName: credentialForm.display_name,
            secret: credential.secret,
          });
        } else {
          toast.success(t('billing.ui.credentials.created'));
        }
      }
      setCredentialDialogOpen(false);
      setCredentialForm(emptyCredentialForm);
      await loadApplication();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function submitDomain(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!application) {
      return;
    }

    const domain = domainForm.domain.trim();
    if (!domain) {
      toast.error(t('billing.ui.domain.url.is.required'));
      return;
    }

    setBusy(true);
    try {
      await saveApplicationDomains([
        ...application.domains.filter((entry) => entry.domain !== domain),
        {
          domain,
          environment: domainForm.environment,
          is_primary: domainForm.is_primary,
        },
      ]);
      toast.success(t('billing.ui.domain.created'));
      setDomainDialogOpen(false);
      setDomainForm(emptyDomainForm);
      await loadApplication();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmDelete() {
    if (!tokens?.access_token || !application || !confirmState) {
      return;
    }

    setBusy(true);
    try {
      if (confirmState.kind === 'application') {
        await billingApi.deleteApplication(tokens.access_token, application.id);
        navigate('/applications');
        return;
      }

      if (confirmState.kind === 'domain' && confirmState.id) {
        await saveApplicationDomains(
          application.domains.filter((entry) => entry.domain !== confirmState.id),
        );
        setDomainDialogOpen(false);
        setDomainForm(emptyDomainForm);
        toast.success(t('billing.ui.domain.deleted'));
      }

      if (confirmState.kind === 'webhook' && confirmState.id) {
        await billingApi.deleteApplicationWebhook(
          tokens.access_token,
          application.id,
          confirmState.id,
        );
        setWebhookDialogOpen(false);
        setWebhookForm(emptyWebhookForm);
        toast.success(t('billing.ui.webhook.deleted'));
      }

      if (confirmState.kind === 'credential' && confirmState.id) {
        await billingApi.deleteApplicationCredential(
          tokens.access_token,
          application.id,
          confirmState.id,
        );
        setCredentialDialogOpen(false);
        setCredentialForm(emptyCredentialForm);
        toast.success(t('billing.ui.credentials.deleted'));
      }

      setConfirmState(null);
      await loadApplication();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  if (!tokens) {
    return (
      <section className="panel page-panel">
        {t('billing.ui.your.session.is.missing.or.expired')}
      </section>
    );
  }

  if (status === 'loading') {
    return <section className="panel page-panel">{t('billing.ui.loading.application')}</section>;
  }

  if (status === 'error' || !application) {
    return (
      <section className="page">
        <PageHeader
          breadcrumbs={<Link to="/applications">{t('billing.nav.applications')}</Link>}
          title={t('billing.ui.application.not.found')}
        />
        <section className="panel panel-error page-panel">
          {banner?.message ?? t('billing.ui.the.requested.application.could.not.be.loaded')}
        </section>
      </section>
    );
  }

  return (
    <section className="page" data-testid="billing-application-manage-page">
      <PageHeader
        breadcrumbs={
          <>
            <Link to="/applications">{t('billing.nav.applications')}</Link>
            <span>/</span>
            <span>{application.display_name}</span>
          </>
        }
        description={t(
          'billing.ui.manage.application.metadata.webhook.endpoints.and.service.credentials.from.one.place',
        )}
        title={application.display_name}
        actions={
          <Button
            onClick={() => navigate(`/products?application_id=${application.id}`)}
            type="button"
            variant="secondary"
          >
            {t('billing.ui.view.products')}
          </Button>
        }
      />

      <Section title={t('billing.ui.basic.information')}>
        <form onSubmit={(event) => void submitApplication(event)}>
          <ApplicationFormFields form={applicationForm} onChange={setApplicationForm} />
          <div className="dialog-actions">
            <Button disabled={busy} type="submit">
              {t('billing.ui.save.application')}
            </Button>
            <Button
              disabled={busy}
              onClick={() => setConfirmState({ kind: 'application' })}
              type="button"
              variant="danger"
            >
              {t('billing.ui.delete.application')}
            </Button>
          </div>
        </form>
      </Section>

      <Section
        actions={
          <Button
            onClick={() => {
              setDomainForm(emptyDomainForm);
              setDomainDialogOpen(true);
            }}
            type="button"
          >
            {t('billing.ui.create.domain')}
          </Button>
        }
        title={t('billing.column.domains')}
      >
        <DataTable
          columns={domainColumns}
          emptyMessage={t('billing.ui.no.domains.are.configured')}
          pageSize={5}
          rows={application.domains}
          searchable
          searchPlaceholder={t('billing.ui.search.domains')}
        />
      </Section>

      <Section
        actions={
          <Button
            onClick={() => {
              setWebhookForm(emptyWebhookForm);
              setWebhookDialogOpen(true);
            }}
            type="button"
          >
            {t('billing.ui.create.webhook')}
          </Button>
        }
        title={t('billing.ui.webhooks')}
      >
        <DataTable
          columns={webhookColumns}
          emptyMessage={t('billing.ui.no.webhook.endpoints.are.configured')}
          pageSize={5}
          rows={application.webhook_endpoints}
          searchable
          searchPlaceholder={t('billing.ui.search.webhooks')}
        />
      </Section>

      <Section
        actions={
          <Button
            onClick={() => {
              setCredentialForm(emptyCredentialForm);
              setCredentialDialogOpen(true);
            }}
            type="button"
          >
            {t('billing.ui.create.credentials')}
          </Button>
        }
        title={t('billing.ui.service.credentials')}
      >
        <DataTable
          columns={credentialColumns}
          emptyMessage={t('billing.ui.no.service.credentials.are.configured')}
          pageSize={5}
          rows={application.service_clients}
          searchable
          searchPlaceholder={t('billing.ui.search.credentials')}
        />
      </Section>

      <EntityManageDialog
        onClose={() => {
          setWebhookDialogOpen(false);
          setWebhookForm(emptyWebhookForm);
        }}
        open={webhookDialogOpen}
        size="large"
        title={webhookForm.id ? t('billing.ui.manage.webhook') : t('billing.ui.create.webhook')}
      >
        <form onSubmit={(event) => void submitWebhook(event)}>
          <div className="form-grid">
            <div className="form-row form-row--split">
              <Field label={t('billing.column.environment')}>
                <Select
                  allowClear={false}
                  onChange={(value) =>
                    setWebhookForm((current) => ({
                      ...current,
                      environment: value ?? 'prod',
                    }))
                  }
                  options={environmentOptions}
                  value={webhookForm.environment}
                />
              </Field>
              <Field label={t('billing.column.status')}>
                <Select
                  allowClear={false}
                  onChange={(value) =>
                    setWebhookForm((current) => ({
                      ...current,
                      status: value ?? 'active',
                    }))
                  }
                  options={webhookStatusOptions}
                  value={webhookForm.status}
                />
              </Field>
            </div>
            <Field label={t('billing.ui.endpoint.url')}>
              <TextInput
                onChange={(event) =>
                  setWebhookForm((current) => ({
                    ...current,
                    url: event.target.value,
                  }))
                }
                placeholder="https://app.example.com/webhooks/billing"
                type="url"
                value={webhookForm.url}
              />
            </Field>
            <Field
              description={t('billing.ui.add.one.billing.event.type.per.line')}
              label={t('billing.ui.event.types')}
            >
              <TextArea
                onChange={(event) =>
                  setWebhookForm((current) => ({
                    ...current,
                    subscribedEventTypesText: event.target.value,
                  }))
                }
                placeholder="subscription.updated"
                rows={5}
                value={webhookForm.subscribedEventTypesText}
              />
            </Field>
            <Field
              description={
                webhookForm.id
                  ? t('billing.ui.leave.empty.to.keep.the.current.signing.secret')
                  : t('billing.ui.the.signing.secret.is.required.for.new.webhooks')
              }
              label={t('billing.ui.signing.secret')}
            >
              <TextInput
                onChange={(event) =>
                  setWebhookForm((current) => ({
                    ...current,
                    signingSecret: event.target.value,
                  }))
                }
                placeholder={t('billing.ui.enter.a.strong.signing.secret')}
                value={webhookForm.signingSecret}
              />
            </Field>
          </div>
          <div className="dialog-actions">
            <Button disabled={busy} type="submit">
              {webhookForm.id ? t('billing.ui.save.webhook') : t('billing.ui.create.webhook')}
            </Button>
            {webhookForm.id ? (
              <Button
                disabled={busy}
                onClick={() =>
                  setConfirmState({
                    kind: 'webhook',
                    id: webhookForm.id ?? undefined,
                  })
                }
                type="button"
                variant="danger"
              >
                {t('billing.ui.delete.webhook')}
              </Button>
            ) : null}
            <Button
              disabled={busy}
              onClick={() => {
                setWebhookDialogOpen(false);
                setWebhookForm(emptyWebhookForm);
              }}
              type="button"
              variant="secondary"
            >
              {t('billing.ui.cancel')}
            </Button>
          </div>
        </form>
      </EntityManageDialog>

      <EntityManageDialog
        onClose={() => {
          setCredentialDialogOpen(false);
          setCredentialForm(emptyCredentialForm);
        }}
        open={credentialDialogOpen}
        size="large"
        title={
          credentialForm.id
            ? t('billing.ui.manage.credentials')
            : t('billing.ui.create.credentials')
        }
      >
        <form onSubmit={(event) => void submitCredential(event)}>
          <div className="form-grid">
            <div className="form-row form-row--split">
              <Field label={t('billing.column.displayName')}>
                <TextInput
                  onChange={(event) =>
                    setCredentialForm((current) => ({
                      ...current,
                      display_name: event.target.value,
                    }))
                  }
                  placeholder={t('billing.ui.primary.api.client')}
                  value={credentialForm.display_name}
                />
              </Field>
              <Field label={t('billing.column.environment')}>
                <Select
                  allowClear={false}
                  onChange={(value) =>
                    setCredentialForm((current) => ({
                      ...current,
                      environment: value ?? 'prod',
                    }))
                  }
                  options={environmentOptions}
                  value={credentialForm.environment}
                />
              </Field>
            </div>
            <div className="form-row form-row--split">
              <Field label={t('billing.column.status')}>
                <Select
                  allowClear={false}
                  onChange={(value) =>
                    setCredentialForm((current) => ({
                      ...current,
                      status: value ?? 'active',
                    }))
                  }
                  options={credentialStatusOptions}
                  value={credentialForm.status}
                />
              </Field>
            </div>
            <Field
              description={t('billing.ui.choose.the.scopes.this.credential.is.allowed.to.use')}
              label={t('billing.ui.allowed.scopes')}
            >
              <div className="entity-stack">
                {credentialScopeOptions.map((scope) => (
                  <CheckboxField
                    checked={selectedCredentialScopes.has(scope)}
                    key={scope}
                    label={scope}
                    onChange={(checked) =>
                      setCredentialForm((current) => {
                        const selected = new Set(parseCredentialScopes(current.allowedScopesText));
                        if (checked) {
                          selected.add(scope);
                        } else {
                          selected.delete(scope);
                        }
                        return {
                          ...current,
                          allowedScopesText: credentialScopeOptions
                            .filter((option) => selected.has(option))
                            .join('\n'),
                        };
                      })
                    }
                  />
                ))}
              </div>
            </Field>
          </div>
          <div className="dialog-actions">
            <Button disabled={busy} type="submit">
              {credentialForm.id
                ? t('billing.ui.save.credentials')
                : t('billing.ui.create.credentials')}
            </Button>
            {credentialForm.id ? (
              <Button
                disabled={busy}
                onClick={() =>
                  setConfirmState({
                    kind: 'credential',
                    id: credentialForm.id ?? undefined,
                  })
                }
                type="button"
                variant="danger"
              >
                {t('billing.ui.delete.credentials')}
              </Button>
            ) : null}
            <Button
              disabled={busy}
              onClick={() => {
                setCredentialDialogOpen(false);
                setCredentialForm(emptyCredentialForm);
              }}
              type="button"
              variant="secondary"
            >
              {t('billing.ui.cancel')}
            </Button>
          </div>
        </form>
      </EntityManageDialog>

      <EntityManageDialog
        onClose={() => setCredentialSecret(null)}
        open={Boolean(credentialSecret)}
        size="small"
        title={t('billing.ui.credential.created')}
      >
        <div className="entity-stack">
          <p>
            {t('billing.credential.copyNotice', {
              name: credentialSecret?.displayName ?? t('billing.ui.this.client'),
            })}
          </p>
          <Field label={t('billing.ui.credential.secret')}>
            <TextInput readOnly value={credentialSecret?.secret ?? ''} />
          </Field>
          <div className="dialog-actions">
            <Button
              onClick={() => {
                if (!credentialSecret) {
                  return;
                }
                void navigator.clipboard
                  .writeText(credentialSecret.secret)
                  .then(() => toast.success(t('billing.ui.credential.secret.copied')))
                  .catch(() => toast.error(t('billing.ui.could.not.copy.the.credential.secret')));
              }}
              type="button"
              variant="secondary"
            >
              {t('billing.ui.copy.secret')}
            </Button>
            <Button onClick={() => setCredentialSecret(null)} type="button">
              {t('billing.ui.confirm')}
            </Button>
          </div>
        </div>
      </EntityManageDialog>

      <EntityManageDialog
        onClose={() => {
          setDomainDialogOpen(false);
          setDomainForm(emptyDomainForm);
        }}
        open={domainDialogOpen}
        size="small"
        title={t('billing.ui.create.domain')}
      >
        <form onSubmit={(event) => void submitDomain(event)}>
          <div className="form-grid">
            <Field label={t('billing.ui.domain.url')}>
              <TextInput
                onChange={(event) =>
                  setDomainForm((current) => ({
                    ...current,
                    domain: event.target.value,
                  }))
                }
                placeholder="app.example.com"
                value={domainForm.domain}
              />
            </Field>
            <Field label={t('billing.column.environment')}>
              <Select
                allowClear={false}
                onChange={(value) =>
                  setDomainForm((current) => ({
                    ...current,
                    environment: value ?? 'prod',
                  }))
                }
                options={environmentOptions}
                value={domainForm.environment}
              />
            </Field>
            <CheckboxField
              checked={domainForm.is_primary}
              label={t('billing.ui.primary.domain')}
              onChange={(checked) =>
                setDomainForm((current) => ({
                  ...current,
                  is_primary: checked,
                }))
              }
            />
          </div>
          <div className="dialog-actions">
            <Button disabled={busy} type="submit">
              {t('billing.ui.create.domain')}
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                setDomainDialogOpen(false);
                setDomainForm(emptyDomainForm);
              }}
              type="button"
              variant="secondary"
            >
              {t('billing.ui.cancel')}
            </Button>
          </div>
        </form>
      </EntityManageDialog>

      <ConfirmDialog
        busy={busy}
        description={
          confirmState?.kind === 'application'
            ? t(
                'billing.ui.this.will.permanently.remove.the.application.and.all.related.admin.data',
              )
            : confirmState?.kind === 'domain'
              ? t('billing.ui.this.will.permanently.remove.the.domain')
              : confirmState?.kind === 'webhook'
                ? t('billing.ui.this.will.permanently.remove.the.webhook.endpoint')
                : t('billing.ui.this.will.permanently.remove.the.service.credentials')
        }
        onClose={() => setConfirmState(null)}
        onConfirm={() => void handleConfirmDelete()}
        open={Boolean(confirmState)}
        title={
          confirmState?.kind === 'application'
            ? t('billing.ui.delete.application')
            : confirmState?.kind === 'domain'
              ? t('billing.ui.delete.domain')
              : confirmState?.kind === 'webhook'
                ? t('billing.ui.delete.webhook')
                : t('billing.ui.delete.credentials')
        }
      />
    </section>
  );
}
